"use client";

import * as React from "react";
import { Calendar, User, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient?: {
      getCurrentWPQueryParams: () => Record<string, any>;
    };
  }
}

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 animate-pulse py-6">
        <div className="h-6 w-32 bg-muted rounded-md" />
        <div className="h-12 w-3/4 bg-muted rounded-md" />
        <div className="h-5 w-48 bg-muted rounded-md" />
        <div className="aspect-video w-full bg-muted rounded-lg" />
        <div className="space-y-4 pt-4">
          <div className="h-4 w-full bg-muted rounded-md" />
          <div className="h-4 w-11/12 bg-muted rounded-md" />
          <div className="h-4 w-4/5 bg-muted rounded-md" />
          <div className="h-4 w-full bg-muted rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center space-y-4">
        <p className="text-base text-muted-foreground">حدث خطأ أثناء تحميل المقال.</p>
        <Button variant="outline" onClick={() => refetch()}>
          إعادة المحاولة
        </Button>
      </div>
    );
  }

  const post = posts && posts.length > 0 ? posts[0] : null;

  if (!post) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center space-y-5">
        <h2 className="text-2xl font-bold text-foreground">المقال غير متوفر</h2>
        <p className="text-muted-foreground">لم يتم العثور على المقال المطلوب أو تم نقله.</p>
        <Button asChild variant="default">
          <Link to="/BlogPage">
            العودة إلى المدونة
          </Link>
        </Button>
      </div>
    );
  }

  const authorName = post.author?.name || "فريق لمسة احتراف";
  const authorAvatarUrl = post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || "";
  const postDate = post.date
    ? new Date(post.date).toLocaleDateString("ar-SA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <article className="mx-auto max-w-4xl">
      {/* Navigation breadcrumb link back to blog */}
      <div className="mb-6">
        <Button asChild variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
          <Link to="/BlogPage">
            <ArrowRight className="size-4 rtl:rotate-180" />
            <span>العودة للمدونة</span>
          </Link>
        </Button>
      </div>

      {/* Prominent Title at the top */}
      <header className="space-y-4 mb-8">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {post.author?.name && (
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <User className="size-4 text-primary" />
              <span>{post.author.name}</span>
            </span>
          )}

          {postDate && (
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="size-4 text-primary" />
              <time dateTime={post.date}>{postDate}</time>
            </span>
          )}
        </div>
      </header>

      {/* Featured Image */}
      {post.featuredImage && (
        <div className="mb-10 overflow-hidden rounded-lg border border-border shadow-xs bg-muted">
          <img
            src={post.featuredImage}
            alt={post.title || "صورة المقال"}
            loading="lazy"
            decoding="async"
            className="w-full aspect-video object-cover"
          />
        </div>
      )}

      {/* Main Content */}
      <section className="mb-12">
        {post.content ? (
          <div
            className="prose prose-neutral max-w-none text-foreground leading-relaxed space-y-6 [&>p]:text-base [&>p]:leading-relaxed [&>p]:text-foreground [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:mt-8 [&>h2]:mb-4 [&>h2]:text-foreground [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mt-6 [&>h3]:mb-3 [&>h3]:text-foreground [&>ul]:list-disc [&>ul]:ps-6 [&>ul]:space-y-2 [&>ol]:list-decimal [&>ol]:ps-6 [&>ol]:space-y-2 [&>img]:rounded-md [&>img]:my-6 [&>blockquote]:border-s-4 [&>blockquote]:border-primary [&>blockquote]:ps-4 [&>blockquote]:italic [&>blockquote]:text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        ) : post.excerpt ? (
          <div
            className="prose prose-neutral max-w-none text-foreground leading-relaxed"
            dangerouslySetInnerHTML={{ __html: post.excerpt }}
          />
        ) : (
          <p className="text-muted-foreground">لا يوجد محتوى متاح لهذا المقال حالياً.</p>
        )}
      </section>

      {/* Author Information */}
      <Separator className="my-8" />
      <footer className="rounded-lg border border-border bg-card p-6 flex flex-col sm:flex-row items-center sm:items-start gap-4">
        <Avatar className="size-14 border border-border">
          {authorAvatarUrl ? (
            <AvatarImage src={authorAvatarUrl} alt={authorName} />
          ) : null}
          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
            {authorName.slice(0, 2)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 text-center sm:text-start space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            كاتب المقال
          </div>
          <h3 className="text-lg font-semibold text-card-foreground">
            {authorName}
          </h3>
          <p className="text-sm text-muted-foreground leading-normal">
            فريق متخصص في صيانة، تنظيف، وفحص أفران الغاز والكهرباء المنزلية بكفاءة عالية وأمان معتمد في الرياض.
          </p>
        </div>
      </footer>
    </article>
  );
}

export default function SinglePost() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-post-template-section"
      data-nav="light"
      className="bg-background text-foreground py-14 md:py-20 border-b border-border"
    >
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}